import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject, delay, map, of, switchMap } from 'rxjs';

export interface ValidateReferralResponse {
  isValid: boolean;
  isInUse: boolean;
  sessionId: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileCaptureService {

  readonly SESSION_KEY = 'fc-ref-session-id';
  readonly PAYOR_KEY = 'fc-payor-key';

  baseUrl = environment.baseUri;

  private fileUploadStartSubject = new BehaviorSubject<File[] | null>(null);
  fileArray: File[] = [];

  constructor(private http: HttpClient) { }

  private toBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') return value.toLowerCase() === 'true' || value === '1';
    if (typeof value === 'number') return value === 1;
    return false;
  }

  public getReferenceId(referenceId: string | null, payorkey: number, sessionId: string = ''): Observable<ValidateReferralResponse> {
    const params = new HttpParams()
      .set('referenceid', referenceId ?? '')
      .set('payorkey', String(payorkey ?? ''))
      .set('sessionId', sessionId ?? '');

    return this.http.get<any>(this.baseUrl + 'FileCapture/ValidateReferal', { params }).pipe(
      map((r: any) => ({
        isValid: this.toBoolean(r?.isValid ?? r?.IsValid),
        isInUse: this.toBoolean(r?.isInUse ?? r?.IsInUse),
        sessionId: r?.sessionId ?? r?.SessionId ?? sessionId
      }))
    );
  }

  public releaseReferenceLock(referenceId: string | null, payorKey: number, sessionId: string): Observable<any> {
    const params = new HttpParams()
      .set('referenceid', referenceId ?? '')
      .set('payorkey', String(payorKey ?? ''))
      .set('sessionId', sessionId);
    return this.http.get<any>(this.baseUrl + 'FileCapture/ReleaseLock', { params });
  }

  public submitDocument(data: any | null): Observable<boolean> {
    const sessionId = window.sessionStorage.getItem(this.SESSION_KEY) || '';
    const params = new HttpParams().set('sessionId', sessionId);
    return this.http.post<boolean>(this.baseUrl + "FileCapture/Submit?Submit", data, { params });
  }

  public addAttachmentToDB(data: any | null): Observable<number> {
    const sessionId = window.sessionStorage.getItem(this.SESSION_KEY) || '';
    const params = new HttpParams().set('sessionId', sessionId);
    var timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    data.timeZoneName = timeZoneName;
    return this.http.post<number>(this.baseUrl + "FileCapture/AddAttachmentToDB", data, { params });
  }

  public commitFile(attachmentKey: any | null, fileName: any, totalChunks: any): Observable<number> {
    return this.http.get<number>(this.baseUrl + "FileCapture/CommitFile?attachmentKey=" + attachmentKey + "&fileName=" + fileName + "&totalChunks=" + totalChunks);
  }

  public ReviewAndScanAttachmnents(data: any | null): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "FileCapture/ReviewAndScanAttachmnents", data);
  }

  public GetAttachmentStatus(data: any | null): Observable<any> {
    return this.http.post<any>(this.baseUrl + "FileCapture/GetAttachmentStatus", data);
  }

  public pollStatus(interval: number, data: any | null): Observable<any> {
    return this.GetAttachmentStatus(data).pipe(switchMap(status => {
      if (status.filter(x => x.VirusScanStatus == null || x.VirusScanStatus == 0).length == 0) {
        return of(status);
      }
      else {
        return of(status).pipe(delay(interval), switchMap(() => this.pollStatus(interval, data)));
      }
    }));
  }

  public uploadAttachment(data: any | null, sourceAppKey: any, sourceKey: any, activityKey: any, attachmentKey: any, referenceId: any): Observable<number> {
    return this.http.post<number>(this.baseUrl + "FileCapture/UploadAttachment?sourceAppKey=" + sourceAppKey + "&sourceKey=" + sourceKey + "&activityKey=" + activityKey + "&attachmentKey=" + attachmentKey + "&referenceId" + referenceId, data);
  }

  public generateCode(contactEmail: string | null, refid: string | null): Observable<any> {
    return this.http.post<any>(this.baseUrl + "EmailAuth/GenerateCode?contactEmail=" + contactEmail + "&refid=" + refid, null);
  }

  public validateCode(data: any | null): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "EmailAuth/ValidateCode", data);
  }

  public addFilesToDB(data: any | null): Observable<HttpEvent<number>> {
    var timeZoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;
    data.timeZoneName = timeZoneName;
    return this.http.post<number>(this.baseUrl + "FileCapture/addFilesToDB", data, {
      reportProgress: true,
      observe: 'events'
    });
  }

  sendUploadStartData(data: File[]) {
    this.fileUploadStartSubject.next(data);
  }

  getUploadStartData(): File[] | null {
    return this.fileUploadStartSubject.getValue();
  }

  deleteAttachment(data: any | null): Observable<boolean> {
    return this.http.post<boolean>(this.baseUrl + "FileCapture/DeleteAttachment", data);
  }

  public getPayorIdByCode(securityCode: string | null): Observable<number> {
    return this.http.get<number>(this.baseUrl + "FileCapture/GetPayorIdByCode?securityCode=" + securityCode);
  }
}
